import type { ClientInfo, TrackingData, HeartbeatData } from "../types";

let cachedBrowserId: string | null = null;

export function getClientInfo(): ClientInfo {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  const vendor = navigator.vendor || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const memory = (navigator as any).deviceMemory || null;
  const hardwareConcurrency = navigator.hardwareConcurrency || 1;

  const screenInfo = {
    width: screen?.width,
    height: screen?.height,
    availWidth: screen?.availWidth,
    availHeight: screen?.availHeight,
    orientation: (screen?.orientation as any)?.type || "",
    pixelDepth: screen?.pixelDepth,
  };

  let deviceType: "tv" | "mobile" | "tablet" | "desktop" = "desktop";
  let brand = "Unknown";
  let os = "Unknown";
  let model = "";
  let isSmartTV = false;
  let isAndroid = false;
  let isWebView = false;
  let isWebApp = false;

  if (ua.includes("Web0S")) {
    brand = "LG";
    os = "webOS";
    isSmartTV = true;
    deviceType = "tv";
    const webosMatch = ua.match(/Web0S\/([^\s]+)/);
    model = webosMatch ? `webOS ${webosMatch[1]}` : "webOS TV";
  } else if (ua.includes("Tizen")) {
    brand = "Samsung";
    os = "Tizen";
    isSmartTV = true;
    deviceType = "tv";
    const tizenMatch = ua.match(/Tizen\/([^\s]+)/);
    const tvMatch = ua.match(/(?:Smart-TV|SMART-TV|TV)/i) ? "Smart TV" : "";
    model = tizenMatch
      ? `Tizen ${tizenMatch[1]} ${tvMatch}`.trim()
      : "Tizen TV";
  } else if (ua.includes("Philips")) {
    brand = "Philips";
    os = "Saphi";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("Sharp") || ua.includes("AQUOS")) {
    brand = "Sharp";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (
    ua.includes("Android") &&
    (ua.includes("Sony") || vendor.includes("Sony"))
  ) {
    brand = "Sony";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (
    ua.includes("Android") &&
    (ua.includes("NetCast") || ua.includes("LG"))
  ) {
    brand = "LG";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes(" Roku") || ua.includes("Roku/")) {
    brand = "Roku";
    os = "Roku OS";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("AppleTV")) {
    brand = "Apple";
    os = "tvOS";
    isSmartTV = true;
    deviceType = "tv";
  }

  if (ua.includes("Android")) {
    isAndroid = true;
    os = "Android";
    deviceType = /Mobile/.test(ua) ? "mobile" : "tablet";

    if (
      ua.includes("Android") &&
      (maxTouchPoints === 0 ||
        ua.includes("Google TV") ||
        ua.includes("XiaoMi"))
    ) {
      deviceType = "tv";
      isSmartTV = true;
      brand = brand === "Unknown" ? "Android TV" : brand;
    }

    const androidModelMatch = ua.match(/\(([^)]*Android[^)]*)\)/);
    if (androidModelMatch && androidModelMatch[1]) {
      model = androidModelMatch[1];
    }
  }

  if (/iPad|iPhone|iPod/.test(ua)) {
    os = "iOS";
    deviceType = "mobile";
    brand = "Apple";
    if (navigator.maxTouchPoints > 1 && /iPad/.test(ua)) {
      deviceType = "tablet";
    }
  }

  if (!isAndroid && !isSmartTV && !/Mobile/.test(ua)) {
    if (ua.includes("Windows")) {
      os = "Windows";
      deviceType = "desktop";
    } else if (ua.includes("Mac") && !/iPhone/.test(ua)) {
      os = "macOS";
      deviceType = "desktop";
      if (maxTouchPoints > 1) deviceType = "tablet";
    } else if (ua.includes("Linux")) {
      os = "Linux";
      deviceType = "desktop";
    }
  }

  if (brand === "Unknown") {
    if (vendor.includes("Google") || ua.includes("Chrome")) brand = "Google";
    if (vendor.includes("Apple")) brand = "Apple";
    if (vendor.includes("Samsung") || ua.includes("SM-")) brand = "Samsung";
  }

  isWebView = /wv|WebView|Linux; U;/.test(ua);

  if (window?.outerHeight === 0 && window?.outerWidth === 0) {
    isWebView = true;
  }

  isWebApp =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true ||
    window.screen?.orientation?.angle !== undefined;

  return {
    brand,
    os,
    model: model || ua.substring(0, 50) + "...",
    deviceType,
    isSmartTV,
    isAndroid,
    isWebView,
    isWebApp,
    domain: window.location.hostname,
    origin: window.location.origin,
    path: window.location.pathname,
    userAgent: ua,
    vendor,
    platform,
    screen: screenInfo,
    hardwareConcurrency,
    deviceMemory: memory,
    maxTouchPoints,
    language: navigator.language,
    languages: navigator.languages?.join(",") || "",
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || "",
    referrer: document.referrer,
    visibilityState: document.visibilityState,
  };
}

export async function getBrowserID(clientInfo: ClientInfo): Promise<string> {
  if (cachedBrowserId) {
    return cachedBrowserId;
  }

  const fingerprintString = JSON.stringify(clientInfo);

  if (typeof crypto !== "undefined" && crypto.subtle && crypto.subtle.digest) {
    try {
      await crypto.subtle.digest("SHA-256", new Uint8Array([1, 2, 3]));

      let encodedData: BufferSource;
      if (typeof TextEncoder !== "undefined") {
        encodedData = new TextEncoder().encode(fingerprintString);
      } else {
        const utf8 = unescape(encodeURIComponent(fingerprintString));
        const buffer = new Uint8Array(utf8.length);
        for (let i = 0; i < utf8.length; i++) {
          buffer[i] = utf8.charCodeAt(i);
        }
        encodedData = buffer;
      }

      const hashBuffer = await crypto.subtle.digest("SHA-256", encodedData);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      cachedBrowserId = hashHex;
      return hashHex;
    } catch (error) {
      console.warn(
        "[StormcloudVideoPlayer] crypto.subtle.digest not supported, using fallback hash"
      );
    }
  }

  let hash = 0;
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const fallbackHash = Math.abs(hash).toString(16).padStart(8, "0");
  const timestamp = Date.now().toString(16).padStart(12, "0");
  const random = Math.random().toString(16).substring(2, 14).padStart(12, "0");

  cachedBrowserId = (fallbackHash + timestamp + random).padEnd(64, "0");
  return cachedBrowserId;
}

export async function sendInitialTracking(licenseKey?: string): Promise<void> {
  try {
    const clientInfo = getClientInfo();
    const browserId = await getBrowserID(clientInfo);

    const trackingData: TrackingData = {
      browserId,
      ...clientInfo,
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (licenseKey) {
      headers["Authorization"] = `Bearer ${licenseKey}`;
    }

    const response = await fetch(
      "https://adstorm.co/api-adstorm-dev/adstorm/player-tracking/track",
      {
        method: "POST",
        headers,
        body: JSON.stringify(trackingData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    await response.json();
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending initial tracking data:",
      error
    );
  }
}

export async function sendHeartbeat(licenseKey?: string): Promise<void> {
  try {
    const clientInfo = getClientInfo();
    const browserId = await getBrowserID(clientInfo);

    const heartbeatData: HeartbeatData = {
      browserId,
      timestamp: new Date().toISOString(),
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (licenseKey) {
      headers["Authorization"] = `Bearer ${licenseKey}`;
    }

    const response = await fetch(
      "https://adstorm.co/api-adstorm-dev/adstorm/player-tracking/heartbeat",
      {
        method: "POST",
        headers,
        body: JSON.stringify(heartbeatData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    await response.json();
  } catch (error) {
    console.error("[StormcloudVideoPlayer] Error sending heartbeat:", error);
  }
}
